// src/pages/finance/BillingServicesCatalog.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  InputAdornment,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  LocalActivity as ServiceIcon,
  AttachMoney as MoneyIcon,
  Search as SearchIcon,
  Description as DescIcon
} from '@mui/icons-material';

const ServiceForm = ({ open, onClose, onSubmit, service = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    description: '',
    ...service
  });

  useEffect(() => {
    if (open) {
      setFormData({
        name: '',
        price: '',
        description: '',
        ...service
      });
    }
  }, [open, service]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '24px', p: 1 }
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ fontWeight: 800, pb: 1, color: '#0f172a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{service ? 'Editar Servicio' : 'Nuevo Servicio Adicional'}</span>
          <IconButton onClick={onClose} size="small" sx={{ color: '#64748b' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
            <TextField
              fullWidth
              label="Nombre del Servicio"
              name="name"
              placeholder="Ej. Disney+ Standard, Mantenimiento Técnico..."
              value={formData.name}
              onChange={handleChange}
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            />

            <TextField
              fullWidth
              label="Precio Mensual / Base"
              name="price"
              type="number"
              inputProps={{ step: '0.01', min: '0' }}
              placeholder="Ej. 35.00"
              value={formData.price}
              onChange={handleChange}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MoneyIcon sx={{ color: '#64748b' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            />

            <TextField
              fullWidth
              label="Descripción"
              name="description"
              placeholder="Describa el alcance de este servicio..."
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={3}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={onClose}
            sx={{ borderRadius: '12px', textTransform: 'none', color: '#64748b', fontWeight: 600 }}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              px: 4,
              fontWeight: 700,
              backgroundColor: '#6366f1',
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)',
              '&:hover': { backgroundColor: '#4f46e5' }
            }}
          >
            Guardar Servicio
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const BillingServicesCatalog = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [openForm, setOpenForm] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await fetch('/api/finance/billing-services', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setServices(Array.isArray(data) ? data : []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching billing services:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (formData) => {
    try {
      const method = selectedService ? 'PUT' : 'POST';
      const url = selectedService
        ? `/api/finance/billing-services/${selectedService.id}`
        : '/api/finance/billing-services';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al guardar el servicio');
      }

      fetchServices();
      setOpenForm(false);
      setSelectedService(null);
    } catch (error) {
      console.error('Error saving billing service:', error);
      alert(error.message || 'Error al guardar el servicio');
    }
  };

  const handleDelete = async (id) => {
    setErrorMessage('');
    try {
      const response = await fetch(`/api/finance/billing-services/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al eliminar el servicio');
      }

      fetchServices();
      setDeleteConfirmOpen(false);
      setServiceToDelete(null);
    } catch (error) {
      console.error('Error deleting billing service:', error);
      setErrorMessage(error.message);
    }
  };

  const filteredServices = services.filter(service =>
    service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (service.description && service.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <Box sx={{ p: { xs: 1, sm: 1.5, md: 2 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: 2, gap: 1.5 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            Catálogo de Servicios Adicionales 📺
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b', mt: 0.2, fontWeight: 500, display: 'block' }}>
            Administre los servicios complementarios (Netflix, Disney+, mantenimiento) que puede añadir a las facturas de sus clientes.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" sx={{ width: { xs: '100%', md: 'auto' } }}>
          <TextField
            placeholder="Buscar servicios..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#64748b', fontSize: '18px' }} />
                </InputAdornment>
              ),
            }}
            sx={{ 
              width: { xs: '100%', sm: 180, md: 220 }, 
              '& .MuiOutlinedInput-root': { 
                height: '36px',
                borderRadius: '8px', 
                backgroundColor: 'white',
                fontSize: '0.8125rem'
              } 
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedService(null);
              setOpenForm(true);
            }}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              px: 2,
              height: '36px',
              fontSize: '0.875rem',
              fontWeight: 700,
              backgroundColor: '#6366f1',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#4f46e5' },
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            Nuevo Servicio
          </Button>
        </Stack>
      </Box>

      {/* Grid de servicios */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : filteredServices.length === 0 ? (
        <Paper sx={{ p: 6, borderRadius: '20px', textAlign: 'center', bgcolor: '#f8fafc', border: '1px dashed #cbd5e1' }}>
          <ServiceIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
          <Typography variant="h6" color="textPrimary" fontWeight="bold">No se encontraron servicios</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Agregue su primer servicio adicional al catálogo haciendo clic en "Nuevo Servicio".
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={1.5}>
          {filteredServices.map((service) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={service.id}>
              <Card
                sx={{
                  borderRadius: '12px',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.02)',
                  border: '1px solid rgba(0,0,0,0.05)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 6px 20px rgba(99, 102, 241, 0.06)',
                    borderColor: 'rgba(99, 102, 241, 0.2)'
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Box sx={{ p: 0.8, borderRadius: '8px', backgroundColor: 'rgba(99, 102, 241, 0.1)', color: '#6366f1', display: 'flex' }}>
                      <ServiceIcon fontSize="small" />
                    </Box>
                    <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.95rem', lineHeight: 1.2 }}>
                      {service.name}
                    </Typography>
                  </Box>
                  
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#4f46e5', mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '1.15rem' }}>
                    <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#64748b' }}>S/</span>
                    {parseFloat(service.price).toFixed(2)}
                    <span style={{ fontSize: '0.75rem', fontWeight: 500, color: '#94a3b8' }}>/ mes</span>
                  </Typography>

                  <Typography variant="caption" sx={{ color: '#475569', minHeight: '34px', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {service.description || 'Sin descripción.'}
                  </Typography>
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2, pt: 0, justifyContent: 'flex-end', gap: 1 }}>
                  <Tooltip title="Editar">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedService(service);
                        setOpenForm(true);
                      }}
                      sx={{ p: 0.6, color: '#6366f1', backgroundColor: 'rgba(99, 102, 241, 0.06)', '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.12)' } }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Eliminar">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        setServiceToDelete(service);
                        setDeleteConfirmOpen(true);
                        setErrorMessage('');
                      }}
                      sx={{ p: 0.6, backgroundColor: 'rgba(239, 68, 68, 0.06)', '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.12)' } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Formulario */}
      <ServiceForm
        open={openForm}
        onClose={() => {
          setOpenForm(false);
          setSelectedService(null);
        }}
        onSubmit={handleSubmit}
        service={selectedService}
      />

      {/* Confirmar eliminación */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{
          sx: { borderRadius: '20px', p: 1 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1, color: '#0f172a' }}>Eliminar Servicio</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#475569', fontWeight: 500 }}>
            ¿Estás seguro de que deseas eliminar el servicio "{serviceToDelete?.name}"? Esta acción no se puede deshacer.
          </Typography>
          {errorMessage && (
            <Typography variant="body2" color="error" sx={{ mt: 2, fontWeight: 'bold' }}>
              ⚠️ {errorMessage}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            sx={{ borderRadius: '12px', textTransform: 'none', color: '#64748b', fontWeight: 600 }}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => handleDelete(serviceToDelete.id)}
            color="error"
            variant="contained"
            sx={{ borderRadius: '12px', textTransform: 'none', px: 3, fontWeight: 700 }}
          >
            Eliminar del catálogo
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillingServicesCatalog;
